import { z } from "zod";

// Characters per line at ESC/POS font A, per paper width. The Rust renderer
// lays the receipt out in character cells, so the mm-to-columns mapping lives
// here next to the list of sizes the picker offers.
//
// It is only a guess: printers sold as 80mm print 42 or 48 characters a line
// depending on the model, and a guess too wide wraps every full-width line.
// The settings let the till override it; see `printerColumns`.
const pageSizes = ["80mm", "78mm", "76mm", "58mm", "57mm", "44mm"] as const;

const pageSizeColumns: Record<(typeof pageSizes)[number], number> = {
	"80mm": 48,
	"78mm": 48,
	"76mm": 42,
	"58mm": 32,
	"57mm": 32,
	"44mm": 24,
};

const DEFAULT_COLUMNS = 48;

const isPageSize = (value: string): value is (typeof pageSizes)[number] =>
	(pageSizes as readonly string[]).includes(value);

export const columnsForPageSize = (pageSize?: string) =>
	(pageSize && isPageSize(pageSize) && pageSizeColumns[pageSize]) ||
	DEFAULT_COLUMNS;

/** What the characters-per-line picker offers. */
export const columnsOptions = [24, 32, 42, 48, 56, 64].map((value) => ({
	value,
	label: String(value),
}));

/**
 * Characters per line for a saved printer: what the till set, or else the
 * guess from its paper width.
 */
export const printerColumns = (printer?: {
	pageSize?: string;
	columns?: number;
}) => printer?.columns || columnsForPageSize(printer?.pageSize);

/**
 * The test print is a ruler: a rule and a line of digits exactly as wide as
 * the receipt will be. If either wraps onto a second line, fewer characters
 * per line are needed.
 */
export const printTestData = (columns: number): ReceiptLine[] => [
	{ kind: "text", text: "Xanyah", align: "center", bold: true, size: 2 },
	{ kind: "text", text: `${columns}`, align: "center" },
	{ kind: "feed", lines: 1 },
	{ kind: "rule" },
	{
		kind: "columns",
		cols: [{ text: "1234567890".repeat(columns), width: columns }],
	},
	{ kind: "rule" },
	{ kind: "feed", lines: 1 },
	{ kind: "barcode", value: "023456789010" },
	{ kind: "feed", lines: 3 },
	{ kind: "cut" },
];

export const pageSizeOptions = pageSizes.map((value) => ({
	value,
	label: value,
}));

export const settingsSchema = z.object({
	defaultVatRateId: z.string().optional(),
	name: z.string().min(1),
	pageSize: z.enum(pageSizes),
	columns: z.number().int().optional(),
});

export type settingsSchemaType = z.infer<typeof settingsSchema>;
